<template>
  <nav class="sidebar-nav">
    <vue-perfect-scrollbar
      class="scroll-area"
      :settings="psSettings"
      @ps-scroll-y="scrollHandle">
      <ul
        v-if="($route.path.startsWith('/owner') > 0 && !hasAccessUserBase) || noAccess"
        class="nav">
        <!-- Dashboard -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess"
          id="menu-item-dashboard"
          :name="$t('message.navigation.dashboard.title')"
          :active="$route.path.startsWith('/owner/dashboard') > 0"
          class="pt-3"
          icon="home"
          route-name="OwnerDashboardOverview"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <!-- Properties -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess"
          id="menu-item-immo"
          :name="$t('message.navigation.realEstate')"
          :active="['/owner/buildings', '/owner/objects', '/owner/tenancies', '/owner/portfolios', '/owner/properties', '/owner/tenant-list'].findIndex(r => $route.path.startsWith(r)) >= 0"
          icon="map-marker-alt"
          route-name="OwnerBuildingsMapView"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-immo-properties"
            data-cy="properties-menu-item"
            :child="true"
            :name="$t('message.navigation.management.properties')"
            route-name="OwnerPropertyView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-immo-buildings"
            data-cy="buildings-menu-item"
            :child="true"
            :name="$t('message.navigation.management.buildings')"
            route-name="OwnerBuildingsView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            v-if="objectTypes && objectTypes.hasRentalObjects"
            id="menu-item-immo-rental-objects"
            data-cy="rental-objects-menu-item"
            :child="true"
            :name="$t('message.navigation.management.rentalObjects')"
            route-name="OwnerRentalObjectOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            v-if="objectTypes && objectTypes.hasCondominiumObjects"
            id="menu-item-immo-condo-objects"
            data-cy="condominium-objects-menu-item"
            :child="true"
            :name="$t('message.navigation.management.allCondominiumObjects')"
            route-name="OwnerCondominiumObjectOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            v-if="objectTypes && objectTypes.hasRentalObjects"
            id="menu-item-immo-tenancies"
            data-cy="tenancies-menu-item"
            :child="true"
            :name="$t('message.navigation.management.tenants')"
            route-name="OwnerTenanciesView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            v-if="objectTypes && objectTypes.hasCondominiumObjects"
            id="menu-item-immo-condo"
            data-cy="condo-owners-menu-item"
            :child="true"
            :name="$t('message.navigation.management.allCondominiumOwners')"
            route-name="OwnerCondominiumOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr
          v-if="!noAccess && isTicketEnabled"
          class="sidebar-hr">
        <!-- Tickets -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isTicketEnabled"
          id="menu-item-tickets"
          data-cy="tickets-menu-item"
          :name="$t('message.navigation.tickets')"
          route-name="OwnerTicketOverview"
          :active="$route.path.startsWith('/owner/tickets') > 0"
          icon="ticket"
          :badge="countTickets"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-tickets-draft"
            data-cy="draft-tickets-menu-item"
            :child="true"
            :name="$t('message.navigation.draftTickets')"
            route-name="OwnerDraftTicketOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-tickets-closed"
            data-cy="closed-tickets-menu-item"
            :child="true"
            :name="$t('message.navigation.closedTickets')"
            route-name="OwnerClosedTicketOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr
          v-if="!noAccess"
          class="sidebar-hr">
        <!-- Reminder -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess"
          id="menu-item-reminder"
          data-cy="reminder-menu-item"
          :name="$t('message.navigation.reminder')"
          route-name="OwnerRemindersView"
          :active="$route.path.startsWith('/owner/reminders') > 0"
          icon="bell"
          :badge="countReminders"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess"
          class="sidebar-hr">
        <!-- Messages -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isEditor"
          id="menu-item-messages"
          data-cy="messages-menu-item"
          :name="$t('message.navigation.messages')"
          route-name="OwnerMessagesView"
          :active="$route.path.startsWith('/owner/messages') > 0"
          icon="envelope"
          :badge="limitedUnreadMails"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-draft-message"
            data-cy="draft-messages-menu-item"
            :child="true"
            :name="$t('message.navigation.draftMessages')"
            route-name="OwnerDraftMessagesView"
            :badge="numberDraftMessage"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr
          v-if="!noAccess && isEditor"
          class="sidebar-hr">
        <!-- Calendar -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isEditor"
          id="menu-item-calendar"
          data-cy="calendar-menu-item"
          :name="$t('message.navigation.calendar.title')"
          icon="calendar-alt"
          :active="$route.path.startsWith('/owner/calendar') > 0"
          route-name="OwnerCalendarOverview"
          :route-param="{ viewCalendar: lastUsedCalendarView, dayToFocus: null }"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess && isEditor"
          class="sidebar-hr">
        <!-- Contact -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess"
          id="menu-item-contacts"
          data-cy="address-book-menu-item"
          :name="$t('message.navigation.directory')"
          :active="($route.path.startsWith('/owner/contacts') > 0 )"
          icon="address-book"
          route-name="OwnerContactOverview"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess"
          class="sidebar-hr">
        <!-- BriefButler -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isLetterEnabled"
          id="menu-item-briefButler"
          :name="$t('message.navigation.briefButler')"
          :active="($route.path.startsWith('/owner/briefButler') > 0 )"
          icon="briefButler"
          route-name="OwnerBriefButlerOverview"
          :badge="computedNumberBriefButler"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess && isLetterEnabled"
          class="sidebar-hr">
        <template v-if="!noAccess && isEditor && (!isAssetModule || isCompanyAdmin)">
          <!-- NetworkPartner -->
          <coozzy-sidebar-nav-dropdown
            id="menu-item-network-partner"
            data-cy="network-partners-menu-item"
            :name="$t('message.navigation.networkPartner')"
            :active="$route.path.startsWith('/owner/network') > 0"
            icon="users"
            route-name="OwnerNetworkPartnerView"
            @navigate="handleNavItemClick" />
          <hr class="sidebar-hr">
        </template>
        <!-- DeepSign -->
        <coozzy-sidebar-nav-dropdown
          v-if="deepSignEnabled && (isCompanyAdmin || hasDigitalSignatureRole)"
          id="menu-item-deepSigns"
          :name="$t('message.navigation.deepSign')"
          :active="$route.path.includes('/deepSign') > 0"
          route-name="OwnerDeepSignOverview"
          icon="deepSign"
          :badge="countOpenSignRequests"
          @navigate="handleNavItemClick" />
        <hr
          v-if="deepSignEnabled && (isCompanyAdmin || hasDigitalSignatureRole)"
          class="sidebar-hr">
        <!-- Settings -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-settings"
          data-cy="settings-menu-item"
          :name="$t('message.navigation.settings.title')"
          :active="$route.path.includes('/settings') > 0"
          icon="cog"
          route-name="OwnerSettingsView"
          @navigate="handleNavItemClick" />
      </ul>
      <ul
        v-else-if="$route.path.startsWith('/marketing') > 0 && !hasAccessUserBase"
        class="nav">
        <!-- Dashboard -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-dashboard"
          :name="$t('message.navigation.dashboard.title')"
          :active="$route.path.startsWith('/marketing/dashboard') > 0"
          class="pt-3"
          icon="home"
          route-name="MarketingDashboardView"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <!-- Reporting -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-reporting"
          :name="$t('message.navigation.reporting.title')"
          :active="$route.path.startsWith('/marketing/reporting') > 0"
          icon="analytics"
          route-name="MarketingReportingOverview"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <!-- Marketing -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-reporting-marketing"
          data-cy="marketing-menu-item"
          :name="$t('message.navigation.marketing.title')"
          route-name="MarketingObjectsView"
          :active="$route.path.startsWith('/marketing/objects') > 0 || $route.path.startsWith('/marketing/publish-history')"
          icon="bullhorn"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess && isTicketEnabled"
          class="sidebar-hr">
        <!-- Tickets -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isTicketEnabled"
          id="menu-item-tickets"
          :name="$t('message.navigation.tickets')"
          route-name="MarketingTicketOverview"
          :active="$route.path.startsWith('/marketing/tickets') > 0"
          icon="ticket"
          :badge="countTickets"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-tickets-draft"
            :child="true"
            :name="$t('message.navigation.draftTickets')"
            route-name="MarketingDraftTicketOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-tickets-closed"
            :child="true"
            :name="$t('message.navigation.closedTickets')"
            route-name="MarketingClosedTicketOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr class="sidebar-hr">
        <!-- Reminder -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-reminder"
          data-cy="reminder-menu-item"
          :name="$t('message.navigation.reminder')"
          route-name="MarketingRemindersView"
          :active="$route.path.startsWith('/marketing/reminders') > 0"
          icon="bell"
          :badge="countReminders"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess"
          class="sidebar-hr">
        <!-- Messages -->
        <coozzy-sidebar-nav-dropdown
          v-if="isEditor"
          id="menu-item-messages"
          data-cy="messages-menu-item"
          :name="$t('message.navigation.messages')"
          route-name="MarketingMessagesView"
          :active="$route.path.startsWith('/marketing/messages') > 0"
          icon="envelope"
          :badge="limitedUnreadMails"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-draft-message"
            data-cy="draft-messages-menu-item"
            :child="true"
            :name="$t('message.navigation.draftMessages')"
            route-name="MarketingDraftMessagesView"
            :badge="numberDraftMessage"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr
          v-if="isEditor"
          class="sidebar-hr">
        <!-- Calendar -->
        <coozzy-sidebar-nav-dropdown
          v-if="isEditor"
          id="menu-item-calendar"
          data-cy="calendar-menu-item"
          :name="$t('message.navigation.calendar.title')"
          icon="calendar-alt"
          :active="$route.path.startsWith('/marketing/calendar') > 0"
          route-name="MarketingCalendarOverview"
          :route-param="{ viewCalendar: lastUsedCalendarView, dayToFocus: null }"
          @navigate="handleNavItemClick" />
        <hr
          v-if="isEditor"
          class="sidebar-hr">
        <!-- Directory -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-contacts"
          data-cy="address-book-menu-item"
          :name="$t('message.navigation.directory')"
          :active="($route.path.startsWith('/marketing/contacts') > 0 )"
          icon="address-book"
          route-name="MarketingContactOverview"
          @navigate="handleNavItemClick" />
        <hr
          class="sidebar-hr">
        <!-- BriefButler -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isLetterEnabled"
          id="menu-item-briefButler"
          :name="$t('message.navigation.briefButler')"
          :active="($route.path.startsWith('/marketing/briefButler') > 0 )"
          icon="briefButler"
          route-name="MarketingBriefButlerOverview"
          :badge="computedNumberBriefButler"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess && isLetterEnabled"
          class="sidebar-hr">
        <!-- DeepSIgn -->
        <coozzy-sidebar-nav-dropdown
          v-if="deepSignEnabled && (isCompanyAdmin || hasDigitalSignatureRole)"
          id="menu-item-deepSigns"
          :name="$t('message.navigation.deepSign')"
          :active="$route.path.includes('/deepSign') > 0"
          route-name="MarketingDeepSignOverview"
          icon="deepSign"
          :badge="countOpenSignRequests"
          @navigate="handleNavItemClick" />
        <hr
          v-if="deepSignEnabled && (isCompanyAdmin || hasDigitalSignatureRole)"
          class="sidebar-hr">
        <!-- Settings -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-settings"
          data-cy="settings-menu-item"
          :name="$t('message.navigation.settings.title')"
          :active="$route.path.includes('/settings') > 0"
          icon="cog"
          route-name="MarketingSettingsView"
          @navigate="handleNavItemClick" />
      </ul>
      <ul
        v-else-if="$route.path.startsWith('/accounting') > 0 && !hasAccessUserBase"
        class="nav">
        <!-- Dashboard -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-dashboard"
          :name="$t('message.navigation.dashboard.title')"
          :active="$route.path.startsWith('/accounting/dashboard') > 0"
          class="pt-3"
          icon="home"
          route-name="AccountingDashboardView"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <!-- Reporting -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-reporting"
          :name="$t('message.navigation.reporting.title')"
          :active="$route.path.startsWith('/accounting/reporting') > 0"
          icon="analytics"
          route-name="AccountingReportingOverview"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <!-- Properties -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-immo"
          :name="$t('message.navigation.realEstate')"
          :active="['/accounting/buildings', '/accounting/objects', '/accounting/tenancies', '/accounting/portfolios', '/accounting/properties', '/accounting/tenant-list'].findIndex(r => $route.path.startsWith(r)) >= 0"
          icon="map-marker-alt"
          route-name="AccountingBuildingsMapView"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-immo-properties"
            data-cy="properties-menu-item"
            :child="true"
            :name="$t('message.navigation.management.properties')"
            route-name="AccountingPropertyView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-immo-buildings"
            :child="true"
            :name="$t('message.navigation.management.buildings')"
            route-name="AccountingBuildingsView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-immo-rental-objects"
            :child="true"
            :name="$t('message.navigation.management.rentalObjects')"
            route-name="AccountingRentalObjectOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-immo-condo-objects"
            :child="true"
            :name="$t('message.navigation.management.allCondominiumObjects')"
            route-name="AccountingCondominiumObjectOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-immo-tenancies"
            :child="true"
            :name="$t('message.navigation.management.tenants')"
            route-name="AccountingTenanciesView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-immo-condo"
            :child="true"
            :name="$t('message.navigation.management.allCondominiumOwners')"
            route-name="AccountingCondominiumOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-immo-marketing-list"
            :child="true"
            :name="$t('message.navigation.management.marketingList')"
            route-name="AccountingMarketingObjectsView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr
          v-if="!noAccess && isTicketEnabled"
          class="sidebar-hr">
        <!-- Tickets -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isTicketEnabled"
          id="menu-item-tickets"
          :name="$t('message.navigation.tickets')"
          route-name="AccountingTicketOverview"
          :active="$route.path.startsWith('/accounting/tickets') > 0"
          icon="ticket"
          :badge="countTickets"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-tickets-draft"
            :child="true"
            :name="$t('message.navigation.draftTickets')"
            route-name="AccountingDraftTicketOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-tickets-closed"
            :child="true"
            :name="$t('message.navigation.closedTickets')"
            route-name="AccountingClosedTicketOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr class="sidebar-hr">
        <!-- Reminder -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-reminder"
          data-cy="reminder-menu-item"
          :name="$t('message.navigation.reminder')"
          route-name="AccountingRemindersView"
          :active="$route.path.startsWith('/accounting/reminders') > 0"
          icon="bell"
          :badge="countReminders"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess"
          class="sidebar-hr">
        <!-- Messages -->
        <coozzy-sidebar-nav-dropdown
          v-if="isEditor"
          id="menu-item-messages"
          :name="$t('message.navigation.messages')"
          route-name="AccountingMessagesView"
          :active="$route.path.startsWith('/accounting/messages') > 0"
          icon="envelope"
          :badge="limitedUnreadMails"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-draft-message"
            data-cy="draft-messages-menu-item"
            :child="true"
            :name="$t('message.navigation.draftMessages')"
            route-name="AccountingDraftMessagesView"
            :badge="numberDraftMessage"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr
          v-if="isEditor"
          class="sidebar-hr">
        <!-- Calendar -->
        <coozzy-sidebar-nav-dropdown
          v-if="isEditor"
          id="menu-item-calendar"
          data-cy="calendar-menu-item"
          :name="$t('message.navigation.calendar.title')"
          icon="calendar-alt"
          :active="$route.path.startsWith('/accounting/calendar') > 0"
          route-name="AccountingCalendarOverview"
          :route-param="{ viewCalendar: lastUsedCalendarView, dayToFocus: null }"
          @navigate="handleNavItemClick" />
        <hr
          v-if="isEditor"
          class="sidebar-hr">
        <!-- Directory -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-contacts"
          :name="$t('message.navigation.directory')"
          :active="($route.path.startsWith('/accounting/contacts') > 0 )"
          icon="address-book"
          route-name="AccountingContactOverview"
          @navigate="handleNavItemClick" />
        <hr
          class="sidebar-hr">
        <!-- BriefButler -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isLetterEnabled"
          id="menu-item-briefButler"
          :name="$t('message.navigation.briefButler')"
          :active="($route.path.startsWith('/accounting/briefButler') > 0 )"
          icon="briefButler"
          route-name="AccountingBriefButlerOverview"
          :badge="computedNumberBriefButler"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess && isLetterEnabled"
          class="sidebar-hr">
        <!-- DeepSIgn -->
        <coozzy-sidebar-nav-dropdown
          v-if="deepSignEnabled && (isCompanyAdmin || hasDigitalSignatureRole)"
          id="menu-item-deepSigns"
          :name="$t('message.navigation.deepSign')"
          :active="$route.path.includes('/deepSign') > 0"
          route-name="AccountingDeepSignOverview"
          icon="deepSign"
          :badge="countOpenSignRequests"
          @navigate="handleNavItemClick" />
        <hr
          v-if="deepSignEnabled && (isCompanyAdmin || hasDigitalSignatureRole)"
          class="sidebar-hr">
        <!-- Settings -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-settings"
          data-cy="settings-menu-item"
          :name="$t('message.navigation.settings.title')"
          :active="$route.path.includes('/settings') > 0"
          icon="cog"
          route-name="AccountingSettingsView"
          @navigate="handleNavItemClick" />
      </ul>
      <ul
        v-else-if="$route.path.startsWith('/admin') > 0 && !hasAccessUserBase"
        class="nav">
        <!-- Dashboard -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-dashboard"
          :name="$t('message.navigation.dashboard.title')"
          :active="$route.path.startsWith('/admin/dashboard') > 0"
          class="pt-3"
          icon="home"
          route-name="AdminDashboardView"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <!-- Reporting -->
        <coozzy-sidebar-nav-dropdown
          v-if="!isJanitor"
          id="menu-item-reporting"
          :name="$t('message.navigation.reporting.title')"
          :active="$route.path.startsWith('/admin/reporting') > 0"
          icon="analytics"
          route-name="AdminReportingOverview"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!isPlatformEmployee && !isJanitor"
          class="sidebar-hr">
        <!-- Properties -->
        <coozzy-sidebar-nav-dropdown
          v-if="!isPlatformEmployee"
          id="menu-item-immo"
          :name="$t('message.navigation.realEstate')"
          :active="['/admin/buildings', '/admin/objects', '/admin/tenancies', '/admin/portfolios', '/admin/properties', '/admin/tenant-list'].findIndex(r => $route.path.startsWith(r)) >= 0"
          icon="map-marker-alt"
          route-name="AdminBuildingsMapView"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-immo-properties"
            data-cy="properties-menu-item"
            :child="true"
            :name="$t('message.navigation.management.properties')"
            route-name="AdminPropertyView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-immo-buildings"
            data-cy="buildings-menu-item"
            :child="true"
            :name="$t('message.navigation.management.buildings')"
            route-name="AdminBuildingsView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-immo-rental-objects"
            data-cy="rental-objects-menu-item"
            :child="true"
            :name="$t('message.navigation.management.rentalObjects')"
            route-name="AdminRentalObjectOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-immo-condo-objects"
            data-cy="condominium-objects-menu-item"
            :child="true"
            :name="$t('message.navigation.management.allCondominiumObjects')"
            route-name="AdminCondominiumObjectOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            v-if="!isJanitor"
            id="menu-item-immo-tenancies"
            data-cy="tenancies-menu-item"
            :child="true"
            :name="$t('message.navigation.management.tenants')"
            route-name="AdminTenanciesView"
            @navigate="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            v-if="!isJanitor"
            id="menu-item-immo-condo"
            data-cy="condo-owners-menu-item"
            :child="true"
            :name="$t('message.navigation.management.allCondominiumOwners')"
            route-name="AdminCondominiumOverview"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            v-if="!isJanitor"
            id="menu-item-immo-marketing-list"
            :child="true"
            :name="$t('message.navigation.management.marketingList')"
            route-name="AdminMarketingObjectsView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr
          v-if="!noAccess && isTicketEnabled"
          class="sidebar-hr">
        <!-- Tickets -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isTicketEnabled"
          id="menu-item-tickets"
          :name="$t('message.navigation.tickets')"
          route-name="AdminTicketOverview"
          :active="$route.path.startsWith('/admin/tickets') > 0"
          icon="ticket"
          :badge="countTickets"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-tickets-draft"
            :child="true"
            :name="$t('message.navigation.draftTickets')"
            route-name="AdminDraftTicketOverview"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-tickets-closed"
            :child="true"
            :name="$t('message.navigation.closedTickets')"
            route-name="AdminClosedTicketOverview"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr class="sidebar-hr">
        <!-- Reminder -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-reminder"
          data-cy="reminder-menu-item"
          :name="$t('message.navigation.reminder')"
          route-name="AdminRemindersView"
          :active="$route.path.startsWith('/admin/reminders') > 0"
          icon="bell"
          :badge="countReminders"
          @navigate="handleNavItemClick" />
        <hr
          v-if="handoverEnabled"
          class="sidebar-hr">
        <!-- Handovers -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && handoverEnabled"
          id="menu-item-handover"
          data-cy="handover-menu-item"
          :name="$t('message.navigation.handover')"
          route-name="AdminHandoversView"
          :active="$route.path.startsWith('/admin/handovers') > 0"
          icon="handover"
          :badge="countHandoverProtocols"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess"
          class="sidebar-hr">
        <!-- Messages -->
        <coozzy-sidebar-nav-dropdown
          v-if="isEditor"
          id="menu-item-messages"
          data-cy="messages-menu-item"
          :name="$t('message.navigation.messages')"
          route-name="AdminMessagesView"
          :active="$route.path.startsWith('/admin/messages') > 0"
          icon="envelope"
          :badge="limitedUnreadMails"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-draft-message"
            data-cy="draft-messages-menu-item"
            :child="true"
            :name="$t('message.navigation.draftMessages')"
            route-name="AdminDraftMessagesView"
            :badge="numberDraftMessage"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr
          v-if="isEditor"
          class="sidebar-hr">
        <!-- Calendar -->
        <coozzy-sidebar-nav-dropdown
          v-if="isEditor || isJanitor"
          id="menu-item-calendar"
          data-cy="calendar-menu-item"
          :name="$t('message.navigation.calendar.title')"
          icon="calendar-alt"
          :active="$route.path.startsWith('/admin/calendar') > 0"
          route-name="AdminCalendarOverview"
          :route-param="{ viewCalendar: lastUsedCalendarView, dayToFocus: null }"
          @navigate="handleNavItemClick" />
        <hr
          v-if="isEditor || isJanitor"
          class="sidebar-hr">
        <!-- Directory -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-contacts"
          data-cy="address-book-menu-item"
          :name="$t('message.navigation.directory')"
          :active="($route.path.startsWith('/admin/contacts') > 0 )"
          icon="address-book"
          route-name="AdminContactOverview"
          @navigate="handleNavItemClick" />
        <hr
          class="sidebar-hr">
        <!-- BriefButler -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isLetterEnabled"
          id="menu-item-briefButler"
          :name="$t('message.navigation.briefButler')"
          :active="($route.path.startsWith('/admin/briefButler') > 0 )"
          icon="briefButler"
          route-name="AdminBriefButlerOverview"
          :badge="computedNumberBriefButler"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess && isLetterEnabled"
          class="sidebar-hr">
        <!-- DeepSIgn -->
        <coozzy-sidebar-nav-dropdown
          v-if="deepSignEnabled && (isCompanyAdmin || hasDigitalSignatureRole)"
          id="menu-item-deepSigns"
          :name="$t('message.navigation.deepSign')"
          :active="$route.path.includes('/deepSign') > 0"
          route-name="AdminDeepSignOverview"
          icon="deepSign"
          :badge="countOpenSignRequests"
          @navigate="handleNavItemClick" />
        <hr
          v-if="deepSignEnabled && (isCompanyAdmin || hasDigitalSignatureRole)"
          class="sidebar-hr">
        <!-- Settings -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-settings"
          data-cy="settings-menu-item"
          :name="$t('message.navigation.settings.title')"
          :active="$route.path.includes('/settings') > 0"
          icon="cog"
          route-name="AdminSettingsView"
          @navigate="handleNavItemClick" />
      </ul>
      <ul
        v-else-if="$route.path.startsWith('/platform') > 0"
        class="nav">
        <!-- Customers -->
        <coozzy-sidebar-nav-dropdown
          name="Kunden"
          :active="$route.path.startsWith('/platform/customers') > 0"
          class="pt-3"
          icon="user-cog"
          route-name="AccountsView"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <coozzy-sidebar-nav-dropdown
          name="Daten bearbeiten"
          :active="$route.path.startsWith('/platform/edit-data') > 0"
          icon="database"
          route-name="EditDataView"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <coozzy-sidebar-nav-dropdown
          name="Reporting"
          :active="$route.path.startsWith('/platform/reporting') > 0"
          icon="analytics"
          route-name="PlatformReportingView"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <coozzy-sidebar-nav-dropdown
          name="Standardreportings"
          :active="$route.path.startsWith('/platform/module_settings') > 0"
          icon="analytics"
          route-name="PlatformModuleSettingsView"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <coozzy-sidebar-nav-dropdown
          name="Einstellungen"
          :active="$route.path.startsWith('/platform/settings') > 0"
          icon="cog"
          route-name="PlatformSettingsView"
          @navigate="handleNavItemClick" />
      </ul>
      <ul
        v-else-if="($route.path.startsWith('/user') > 0 || $route.name === 'ApplicationFormLandingPage') && hasAccessUserBase"
        class="nav">
        <coozzy-sidebar-nav-dropdown
          :name="$t('message.generic.requests')"
          :active="$route.path.startsWith('/user/requests') > 0"
          class="pt-3"
          icon="house-user"
          route-name="UserRequests"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <coozzy-sidebar-nav-dropdown
          :name="$t('message.generic.submitApplicationForm')"
          :active="$route.path.startsWith('/user/applicationForm') > 0"
          icon="address-card"
          route-name="ApplicationFormView"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <coozzy-sidebar-nav-dropdown
          :name="$t('message.navigation.settings.title')"
          :active="$route.path.startsWith('/user/settings') > 0"
          icon="cog"
          route-name="UserSettings"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
      </ul>
      <ul
        v-else-if="$route.path.startsWith('/asset') > 0 && hasAccessAssetBase"
        class="nav">
        <!-- Dashboard -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-dashboard"
          :name="$t('message.navigation.dashboard.title')"
          :active="$route.path.startsWith('/asset/dashboard') > 0"
          class="pt-3"
          icon="home"
          route-name="AssetDashboardView"
          @navigate="handleNavItemClick" />
        <hr class="sidebar-hr">
        <!-- Properties -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-immo"
          :name="$t('message.navigation.realEstate')"
          :active="['/asset/buildings', '/asset/objects', '/asset/tenancies', '/asset/portfolios', '/asset/properties', '/asset/tenant-list'].findIndex(r => $route.path.startsWith(r)) >= 0"
          icon="map-marker-alt"
          route-name="AssetBuildingsMapView"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-immo-properties"
            data-cy="properties-menu-item"
            :child="true"
            :name="$t('message.navigation.management.properties')"
            route-name="AssetPropertyView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-immo-building"
            :child="true"
            :name="$t('message.navigation.management.buildings')"
            route-name="AssetBuildingsView"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            v-if="objectTypes && objectTypes.hasRentalObjects"
            id="menu-item-immo-rental-objects"
            :child="true"
            :name="$t('message.navigation.management.rentalObjects')"
            route-name="AssetRentalObjectOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            v-if="objectTypes && objectTypes.hasCondominiumObjects"
            id="menu-item-immo-condo-objects"
            :child="true"
            :name="$t('message.navigation.management.allCondominiumObjects')"
            route-name="AssetCondominiumObjectOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            v-if="objectTypes && objectTypes.hasRentalObjects"
            id="menu-item-immo-tenancies"
            :child="true"
            :name="$t('message.navigation.management.tenants')"
            route-name="AssetTenanciesView"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            v-if="objectTypes && objectTypes.hasCondominiumObjects"
            id="menu-item-immo-condo"
            :child="true"
            :name="$t('message.navigation.management.allCondominiumOwners')"
            route-name="AssetCondominiumOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr
          v-if="!noAccess && isTicketEnabled"
          class="sidebar-hr">
        <!-- Tickets -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isTicketEnabled"
          id="menu-item-tickets"
          :name="$t('message.navigation.tickets')"
          route-name="AssetTicketOverview"
          :active="$route.path.startsWith('/asset/tickets') > 0"
          icon="ticket"
          :badge="countTickets"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-tickets-draft"
            :child="true"
            :name="$t('message.navigation.draftTickets')"
            route-name="AssetDraftTicketOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
          <coozzy-sidebar-nav-link
            id="menu-item-tickets-closed"
            :child="true"
            :name="$t('message.navigation.closedTickets')"
            route-name="AssetClosedTicketOverview"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr class="sidebar-hr">
        <!-- Reminder -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-reminder"
          data-cy="reminder-menu-item"
          :name="$t('message.navigation.reminder')"
          route-name="AssetRemindersView"
          :active="$route.path.startsWith('/asset/reminders') > 0"
          icon="bell"
          :badge="countReminders"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess"
          class="sidebar-hr">
        <!-- Messages -->
        <coozzy-sidebar-nav-dropdown
          v-if="isEditor"
          id="menu-item-messages"
          :name="$t('message.navigation.messages')"
          route-name="AssetMessagesView"
          :active="$route.path.startsWith('/asset/messages') > 0"
          icon="envelope"
          :badge="limitedUnreadMails"
          @navigate="handleNavItemClick">
          <coozzy-sidebar-nav-link
            id="menu-item-draft-message"
            data-cy="draft-messages-menu-item"
            :child="true"
            :name="$t('message.navigation.draftMessages')"
            route-name="AssetDraftMessagesView"
            :badge="numberDraftMessage"
            @navigate="handleNavItemClick"
            @click.native="handleNavItemClick" />
        </coozzy-sidebar-nav-dropdown>
        <hr
          v-if="isEditor"
          class="sidebar-hr">
        <!-- Calendar -->
        <coozzy-sidebar-nav-dropdown
          v-if="isEditor"
          id="menu-item-calendar"
          data-cy="calendar-menu-item"
          :name="$t('message.navigation.calendar.title')"
          icon="calendar-alt"
          :active="$route.path.startsWith('/asset/calendar') > 0"
          route-name="AssetCalendarOverview"
          :route-param="{ viewCalendar: lastUsedCalendarView, dayToFocus: null }"
          @navigate="handleNavItemClick" />
        <hr
          v-if="isEditor"
          class="sidebar-hr">
        <!-- Contact -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-contacts"
          :name="$t('message.navigation.directory')"
          :active="($route.path.startsWith('/asset/contacts') > 0 )"
          icon="address-book"
          route-name="AssetContactOverview"
          @navigate="handleNavItemClick" />
        <hr
          class="sidebar-hr">
        <!-- BriefButler -->
        <coozzy-sidebar-nav-dropdown
          v-if="!noAccess && isLetterEnabled"
          id="menu-item-briefButler"
          :name="$t('message.navigation.briefButler')"
          :active="($route.path.startsWith('/asset/briefButler') > 0 )"
          icon="briefButler"
          route-name="AssetBriefButlerOverview"
          :badge="computedNumberBriefButler"
          @navigate="handleNavItemClick" />
        <hr
          v-if="!noAccess && isLetterEnabled"
          class="sidebar-hr">
        <!-- NetworkPartner -->
        <template v-if="!isAssetModule || isCompanyAdmin">
          <coozzy-sidebar-nav-dropdown
            id="menu-item-network-partner"
            :name="$t('message.navigation.networkPartner')"
            :active="$route.path.startsWith('/asset/network') > 0"
            icon="users"
            route-name="AssetNetworkPartnerView"
            @navigate="handleNavItemClick" />
          <hr class="sidebar-hr">
        </template>
        <!-- DeepSign -->
        <coozzy-sidebar-nav-dropdown
          v-if="deepSignEnabled && (isCompanyAdmin || hasDigitalSignatureRole)"
          id="menu-item-deepSigns"
          :name="$t('message.navigation.deepSign')"
          :active="$route.path.includes('/deepSign') > 0"
          route-name="AssetDeepSignOverview"
          icon="deepSign"
          :badge="countOpenSignRequests"
          @navigate="handleNavItemClick" />
        <hr
          v-if="deepSignEnabled && (isCompanyAdmin || hasDigitalSignatureRole)"
          class="sidebar-hr">
        <!-- settings -->
        <coozzy-sidebar-nav-dropdown
          id="menu-item-settings"
          data-cy="settings-menu-item"
          :name="$t('message.navigation.settings.title')"
          :active="$route.path.includes('/settings') > 0"
          route-name="AssetSettingsView"
          icon="cog"
          @navigate="handleNavItemClick" />
      </ul>
    </vue-perfect-scrollbar>
  </nav>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import CoozzySidebarNavLink from '../framework/components/navigation/coreui/CoozzySidebarNavLink'
import CoozzySidebarNavDropdown from '../framework/components/navigation/coreui/CoozzySidebarNavDropdown'
import { user } from '@/mixins/user'
import { eventBus, mail } from '@/mixins/mail'
import { contract } from '@/mixins/contract'
import { reminder } from '@/mixins/reminder'
import { handover } from '@/mixins/handover'
import ObjectApi from '@/misc/apis/ObjectApi'
import TicketApi from '@/misc/apis/TicketApi'
import { mapActions, mapGetters, mapState } from 'vuex'
import DeepSignApi from '@/misc/apis/DeepSignApi'
import { deepSign } from '@/mixins/deepSign'
import { ticket } from '@/mixins/ticket'
import BriefButlerApi from '@/misc/apis/BriefButlerApi'
import InvestPlanApi from '@/misc/apis/InvestPlanApi'
import HandoverProtocolApi from '@/misc/apis/HandoverProtocolApi'
import EvorestApi from '@/misc/apis/EvorestApi'
import GocautionApi from '@/misc/apis/GocautionApi'
import FirstcautionApi from '@/misc/apis/FirstcautionApi'
import DeepOApi from '@/misc/apis/DeepOApi'

export default {
  name: 'CoozzySidebarNav',
  components: {
    CoozzySidebarNavDropdown,
    CoozzySidebarNavLink,
    VuePerfectScrollbar
  },
  mixins: [user, mail, reminder, deepSign, contract, ticket, handover],
  data() {
    return {
      objectTypes: null,
      isTicketEnabled: false,
      isLetterEnabled: false,
      handoverEnabled: false,
      interval: null,
      lastUsedCalendarView: 'week',
      lastUsedCalendarViewHandler: null
    }
  },
  computed: {
    ...mapState('message', ['mailsLoadedAt', 'mailsDraftLoadedAt', 'readMails']),
    ...mapState('ticket', ['readTickets', 'unreadTickets']),
    ...mapGetters('message', ['getMailboxesForUserId']),
    mailboxesForCurrentUser() {
      return this.getMailboxesForUserId(this.userId)
    },
    noAccess() {
      return (!this.hasAccessMarketBase && !this.hasAccessPropBase && !this.hasAccessAccountingBase && !this.hasAccessAdminBase && !this.hasAccessUserBase && !this.hasAccessAssetBase)
    },
    psSettings: () => {
      // ToDo: find better rtl fix
      return {
        maxScrollbarLength: 200,
        minScrollbarLength: 40,
        suppressScrollX: getComputedStyle(document.querySelector('html')).direction !== 'rtl',
        wheelPropagation: false,
        interceptRailY: styles => ({
          ...styles,
          height: 0
        })
      }
    },
    countTickets() {
      return this.$store.getters['ticket/getCountTickets']
    },
    countHandovers() {
      return this.$store.getters['ticket/getCountHandovers']
    },
    computedNumberBriefButler() {
      return this.$store.getters['contract/getNumberBriefButler']
    },
    limitedUnreadMails() {
      if (this.numberMessage >= 100) {
        return '99+'
      }
      return this.numberMessage
    }
  },
  watch: {
    mailboxesForCurrentUser: function () {
      this.countUnreadMails(true)
    },
    mailsLoadedAt: function () {
      this.countUnreadMails()
    },
    mailsDraftLoadedAt: function () {
      this.countDraftMails()
    },
    briefButlerLoadedAt: function () {
      this.countBriefButler()
    },
    readMails: function () {
      this.countUnreadMails(true)
    },
    unreadMails: function () {
      this.countUnreadMails()
    },
    numberMessage: function (val, oldVal) {
      if (val > oldVal) {
        this.$store.dispatch('message/notifyNewEmails')
      }
      this.$nextTick(() => {
        this.$store.commit('message/setNewMessagesArrived', false)
      })
    }
  },
  created () {
    if (localStorage.getItem('lastUsedCalendarView')) {
      this.lastUsedCalendarView = localStorage.getItem('lastUsedCalendarView')
    }
    this.lastUsedCalendarViewHandler = (value) => {
      if (value !== '') {
        this.lastUsedCalendarView = value
      }
    }
    eventBus.$on('lastUsedCalendarView', this.lastUsedCalendarViewHandler)
  },
  mounted: function () {
    this.loadMailboxesForUserId(this.userId)
    this.countDraftMails()
    if (this.$store.getters['contract/getNumberBriefButler'] === undefined || this.$store.getters['contract/getNumberBriefButler'] === '-') {
      this.countBriefButler()
    }
    ObjectApi.getObjectTypes(this.accountId)
      .then(response => {
        this.objectTypes = response
      })
    TicketApi.hasTicketingEnabled(this.accountId)
      .then(response => {
        this.isTicketEnabled = response.enabled
        this.$store.dispatch('user/setIsTicketingEnabled', response.enabled)
        if (response.enabled === true) {
          this.getCountMyUnreadTickets(true)
        }
      })
    HandoverProtocolApi.hasHandoverProtocolEnabled(this.accountId)
      .then(response => {
        this.handoverEnabled = response.enabled
        this.$store.dispatch('user/setIsHandoverProtocolEnabled', response.enabled)
      })
    DeepOApi.isDeepOEnabled(this.accountId).then((response) => {
        this.deepOEnabled = response.enabled
        this.$store.dispatch('user/setIsDeeopOEnabled', response.enabled)
      }).catch((error) => {
        console.log(error)
        this.loading = false
      })
    setTimeout(() => {
      if (this.$store.getters['user/getIsBriefButlerEnabled'] === null) {
        BriefButlerApi.loadSettings(this.accountId)
          .then(response => {
            this.isLetterEnabled = response.enabled
            this.$store.dispatch('user/setIsBriefButlerEnabled', response.enabled)
          })
          .catch(e => {
            console.error(e)
          })
      } else {
        this.isLetterEnabled = this.$store.getters['user/getIsBriefButlerEnabled']
      }
    }, this.$route.name.search('BriefButler') > 0 ? 2000 : 0)
    DeepSignApi.isDeepSignEnabled(this.accountId).then((response) => {
      this.$store.dispatch('deepSign/setDeepSignStatus', response.enabled)
    })

    Promise.all([
      EvorestApi.hasEvorestEnabled(this.accountId).catch(() => ({ enabled: false })),
      GocautionApi.hasEnabled(this.accountId).catch(() => ({ enabled: false })),
      FirstcautionApi.hasEnabled(this.accountId).catch(() => ({ enabled: false }))
    ]).then(([evorestStatus, goCautionStatus, firstcautionStatus]) => {
      this.$store.dispatch('digitalDeposit/setDigitalDepositStatus', !!evorestStatus?.enabled || !!goCautionStatus?.enabled || !!firstcautionStatus?.enabled)
    })

    // Calculate the delay until the next minute
    const now = new Date()
    const delay = 60 - now.getSeconds()
    // we make this setTimeout to wait until the next minute to start the interval to be sure that this function + filterMessages in messageView executed in the same time
    setTimeout(() => {
      this.interval = setInterval(() => this.countUnreadMails(), 30000)
    }, delay * 1000)
  },
  beforeDestroy() {
    clearInterval(this.interval)
    if (this.lastUsedCalendarViewHandler) {
      eventBus.$off('lastUsedCalendarView', this.lastUsedCalendarViewHandler)
    }
  },
  methods: {
    ...mapActions('message', ['loadMailboxesForUserId']),
    scrollHandle() {
    },
    isMobileOrTablet() {
      // You may want to use a more robust check depending on your breakpoints
      return window.innerWidth <= 1024
    },
    handleNavItemClick(text) {
      console.log('nav item clicked')
      if (this.isMobileOrTablet()) {
        console.log('is mobile or tablet')
        this.$emit('close-sidebar', text) // Notify parent to close the sidebar
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.scroll-area {
  position: absolute;
  height: 100%;
  margin: auto;
}

.sidebar-hr {
  margin: 4px 0 4px 0;
  border-color: rgba(255, 255, 255, .6);
}

.nav {
  width: 280px !important;
  background-color: rgba(0, 68, 95, 1) !important;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, .2))
}
</style>
